/**
 * GraphWidget - Visualisation de graphe relationnel avec D3.js
 * 
 * Affiche les documents sous forme de graphe avec leurs relations.
 * Utilise D3-force pour la simulation de force et le rendu SVG.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
} from "d3-force";
import { select, Selection } from "d3-selection";
import { drag } from "d3-drag";
import { zoom, zoomIdentity } from "d3-zoom";
import { Content } from "@/models/document";
import { Typography } from "@/components/ui";
import { FaProjectDiagram } from "react-icons/fa";
import { PostDetailModal } from "@/components/modals";
import { Z_INDEX, zIndexClass } from "@/utils/z-index";
import type { DocumentsData, GraphVisualConfig } from "@/models/widget-v2";
import { chartColors, vibrantColors } from "@/styles/colors";

// ============================================================================
// Types
// ============================================================================

interface GraphNode {
  id: string;
  document: Content;
  type: string;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number;
  fy?: number;
}

interface GraphLink {
  source: string | GraphNode;
  target: string | GraphNode;
  type?: string;
}

interface GraphWidgetProps {
  data: DocumentsData;
  visual: GraphVisualConfig;
  title?: string;
}

// ============================================================================
// Color Utilities
// ============================================================================

const DEFAULT_COLOR_PALETTE: Record<string, string> = {
  post: chartColors.blue,
  actor: chartColors.green,
  author: vibrantColors[0], // amber
  destination: chartColors.violet,
  parent: chartColors.red,
  source: chartColors.teal,
  reply_to: chartColors.pink,
  retweet_of: chartColors.teal,
  quote_of: chartColors.orange,
  in_reply_to: "#A855F7", // purple variant not in chartColors
  thread_parent: "#6366F1", // indigo accent
};

function getNodeColorByType(type: string, customPalette?: Record<string, string>): string {
  const colorPalette = { ...DEFAULT_COLOR_PALETTE, ...customPalette };
  
  if (colorPalette[type]) {
    return colorPalette[type];
  }
  
  // Generate deterministic color from hash
  let hash = 0;
  for (let i = 0; i < type.length; i++) {
    hash = type.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

// ============================================================================
// Main Component
// ============================================================================

export const GraphWidget: React.FC<GraphWidgetProps> = ({
  data,
  visual,
  title,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: string } | null>(null);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);

  // Configuration depuis visual avec valeurs par défaut
  const nodeSize = visual.nodeSize ?? 15;
  const linkDistance = visual.linkDistance ?? 30;
  const chargeStrength = visual.chargeStrength ?? -200;
  const linkColor = "#94A3B8";
  const showLabels = visual.showLabels ?? true;
  const showTooltips = visual.showTooltips ?? true;
  const draggable = true;

  // Extract documents from data
  const documents = useMemo(() => {
    if (!data?.documents) return [];
    return data.documents as unknown as Content[];
  }, [data]);

  // Build nodes and links from documents
  const { nodes, links } = useMemo(() => {
    if (!documents || documents.length === 0) {
      return { nodes: [], links: [] };
    }

    const allNodesMap = new Map<string, GraphNode>();
    const parentChildLinks: Array<{ parentId: string; childId: string; linkType: string }> = [];

    // Create nodes from main documents
    documents
      .filter((doc) => doc.id)
      .forEach((doc) => {
        allNodesMap.set(doc.id, {
          id: doc.id,
          document: doc,
          type: "post",
        });
      });

    // Recursive function to find sub-documents
    const findAndCreateSubDocuments = (
      obj: any,
      parentId: string,
      path: string[] = [],
      maxDepth: number = 10,
      currentDepth: number = 0
    ): void => {
      if (currentDepth >= maxDepth || !obj || typeof obj !== "object") return;

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item && typeof item === "object") {
            if (item.id && typeof item.id === "string" && item.id.length > 0) {
              const subDocId = item.id;
              const nodeType = path.length > 0 ? path[path.length - 1] : "array_item";
              
              if (!allNodesMap.has(subDocId)) {
                allNodesMap.set(subDocId, {
                  id: subDocId,
                  document: { id: subDocId, ...item } as Content,
                  type: nodeType,
                });
              }
              
              parentChildLinks.push({
                parentId,
                childId: subDocId,
                linkType: path.length > 0 ? path.join(".") : "array_item",
              });
              
              findAndCreateSubDocuments(item, subDocId, [...path, index.toString()], maxDepth, currentDepth + 1);
            } else {
              findAndCreateSubDocuments(item, parentId, [...path, index.toString()], maxDepth, currentDepth + 1);
            }
          }
        });
        return;
      }

      for (const key in obj) {
        if (!obj.hasOwnProperty(key)) continue;
        const value = obj[key];
        const currentPath = [...path, key];

        if (value && typeof value === "object" && !Array.isArray(value) && value.id && typeof value.id === "string" && value.id.length > 0) {
          const subDocId = value.id;
          if (subDocId !== parentId) {
            if (!allNodesMap.has(subDocId)) {
              allNodesMap.set(subDocId, {
                id: subDocId,
                document: { id: subDocId, ...value } as Content,
                type: key,
              });
            }
            parentChildLinks.push({
              parentId,
              childId: subDocId,
              linkType: path.length > 0 ? path.join(".") : key,
            });
            findAndCreateSubDocuments(value, subDocId, currentPath, maxDepth, currentDepth + 1);
          }
        } else if (value && typeof value === "object") {
          findAndCreateSubDocuments(value, parentId, currentPath, maxDepth, currentDepth + 1);
        }
      }
    };

    documents.forEach((doc) => {
      if (!doc.id) return;
      findAndCreateSubDocuments(doc, doc.id);
    });

    const graphNodes: GraphNode[] = Array.from(allNodesMap.values());

    // Find IDs in object
    const findIdsInObject = (
      obj: any,
      path: string[] = [],
      maxDepth: number = 10,
      currentDepth: number = 0
    ): Array<{ id: string; path: string[]; type: string }> => {
      const results: Array<{ id: string; path: string[]; type: string }> = [];
      if (currentDepth >= maxDepth || !obj || typeof obj !== "object") return results;

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (typeof item === "string" && item.length > 0 && allNodesMap.has(item)) {
            results.push({ id: item, path: [...path, index.toString()], type: path.join(".") || "array_item" });
          } else if (item && typeof item === "object") {
            results.push(...findIdsInObject(item, [...path, index.toString()], maxDepth, currentDepth + 1));
          }
        });
        return results;
      }

      for (const key in obj) {
        if (!obj.hasOwnProperty(key)) continue;
        const value = obj[key];
        const currentPath = [...path, key];

        if (key === "id" && typeof value === "string" && value.length > 0 && allNodesMap.has(value)) {
          results.push({ id: value, path: currentPath, type: path.length > 0 ? path.join(".") : "root" });
        } else if (value && typeof value === "object") {
          results.push(...findIdsInObject(value, currentPath, maxDepth, currentDepth + 1));
        }
      }
      return results;
    };

    // Create links
    const graphLinks: GraphLink[] = [];

    parentChildLinks.forEach(({ parentId, childId, linkType }) => {
      if (parentId !== childId && allNodesMap.has(parentId) && allNodesMap.has(childId)) {
        graphLinks.push({ source: parentId, target: childId, type: linkType });
      }
    });

    graphNodes.forEach((node) => {
      if (!node.id) return;
      const foundIds = findIdsInObject(node.document);
      foundIds.forEach(({ id, type }) => {
        if (id !== node.id) {
          if (allNodesMap.has(id)) {
            graphLinks.push({ source: node.id, target: id, type });
          }
        }
      });
    });

    // Deduplicate links
    const uniqueLinksMap = new Map<string, GraphLink>();
    graphLinks.forEach((link) => {
      const key = `${link.source}-${link.target}`;
      if (!uniqueLinksMap.has(key)) {
        uniqueLinksMap.set(key, link);
      }
    });

    return { nodes: graphNodes, links: Array.from(uniqueLinksMap.values()) };
  }, [documents]);

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width || 800, height: rect.height || 600 });
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  // Draw graph with D3
  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const svg = select(svgRef.current);
    svg.selectAll("*").remove();

    const width = dimensions.width;
    const height = dimensions.height;

    svg.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "transparent")
      .style("cursor", "grab");

    const g = svg.append("g").attr("transform", `translate(${width / 2}, ${height / 2})`);

    const simulation = forceSimulation<GraphNode>(nodes)
      .force("link", forceLink<GraphNode, GraphLink>(links).id((d: GraphNode) => d.id).distance(linkDistance))
      .force("charge", forceManyBody().strength(chargeStrength))
      .force("center", forceCenter(0, 0))
      .force("collision", forceCollide().radius(nodeSize + 5));

    const link = g.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(links)
      .enter()
      .append("line")
      .attr("stroke", linkColor)
      .attr("stroke-opacity", 0.7)
      .attr("stroke-width", 2.5);

    const node = g.append("g")
      .attr("class", "nodes")
      .selectAll("circle")
      .data(nodes)
      .enter()
      .append("circle")
      .attr("r", nodeSize)
      .attr("fill", (d: GraphNode) => getNodeColorByType(d.type))
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .call(
        draggable
          ? drag<SVGCircleElement, GraphNode>()
              .on("start", (event: any, d: GraphNode) => {
                if (!event.active) simulation.alphaTarget(0.3).restart();
                d.fx = d.x;
                d.fy = d.y;
              })
              .on("drag", (event: any, d: GraphNode) => {
                d.fx = event.x;
                d.fy = event.y;
              })
              .on("end", (event: any, d: GraphNode) => {
                if (!event.active) simulation.alphaTarget(0);
                d.fx = undefined;
                d.fy = undefined;
              })
          : (null as any)
      );

    let labels: Selection<SVGTextElement, GraphNode, SVGGElement, unknown> | null = null;
    if (showLabels) {
      // Get computed text color from CSS variable for dark mode support
      const computedStyle = getComputedStyle(document.documentElement);
      const textColor = computedStyle.getPropertyValue("--color-content-primary").trim() || "#374151";
      
      labels = g.append("g")
        .attr("class", "labels")
        .selectAll("text")
        .data(nodes)
        .enter()
        .append("text")
        .text((d) => {
          const doc = d.document as any;
          return doc.name || doc.normalized?.name || doc.normalized?.title || doc.normalized?.username || d.id.substring(0, 10) || "Document";
        })
        .attr("font-size", "14px")
        .attr("font-weight", "500")
        .attr("fill", textColor)
        .attr("text-anchor", "middle")
        .attr("dy", nodeSize + 18)
        .style("pointer-events", "none");
    }

    if (showTooltips) {
      node
        .on("mouseover", (event, d) => {
          const doc = d.document as any;
          const content = [
            `Type: ${d.type}`,
            doc.name && `Nom: ${doc.name}`,
            `ID: ${d.document.id}`,
            d.document.normalized?.title && `Titre: ${d.document.normalized.title}`,
            d.document.normalized?.username && `Auteur: ${d.document.normalized.username}`,
          ].filter(Boolean).join("\n");

          const rect = svgRef.current?.getBoundingClientRect();
          setTooltip({
            x: rect ? event.clientX - rect.left : event.clientX,
            y: rect ? event.clientY - rect.top : event.clientY,
            content,
          });
        })
        .on("mouseout", () => setTooltip(null))
        .on("click", (event, d) => {
          event.stopPropagation();
          setSelectedContent(d.document);
        });
    }

    // Setup zoom behavior first
    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => g.attr("transform", event.transform.toString()));

    svg.call(zoomBehavior);

    // Function to fit the graph to the viewport
    const fitToView = () => {
      if (nodes.length === 0) return;
      
      // Calculate bounds of all nodes
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      nodes.forEach(n => {
        const x = n.x ?? 0;
        const y = n.y ?? 0;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      });
      
      // Add padding for node size and labels
      const padding = nodeSize * 3;
      minX -= padding;
      maxX += padding;
      minY -= padding;
      maxY += padding;
      
      const graphWidth = maxX - minX;
      const graphHeight = maxY - minY;
      
      if (graphWidth <= 0 || graphHeight <= 0) return;
      
      // Calculate scale to fit
      const scale = Math.min(
        width / graphWidth,
        height / graphHeight,
        2 // Max scale to avoid too much zoom
      ) * 0.85; // Leave some margin
      
      // Calculate center of the graph
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      
      // Apply transform to center and scale
      const transform = zoomIdentity
        .translate(width / 2, height / 2)
        .scale(scale)
        .translate(-centerX, -centerY);
      
      svg.call(zoomBehavior.transform, transform);
    };

    simulation.on("tick", () => {
      link
        .attr("x1", (d) => (d.source as GraphNode).x || 0)
        .attr("y1", (d) => (d.source as GraphNode).y || 0)
        .attr("x2", (d) => (d.target as GraphNode).x || 0)
        .attr("y2", (d) => (d.target as GraphNode).y || 0);

      node.attr("cx", (d) => d.x || 0).attr("cy", (d) => d.y || 0);

      if (labels) {
        labels.attr("x", (d) => d.x || 0).attr("y", (d) => d.y || 0);
      }
    });
    
    // Fit to view when simulation ends
    simulation.on("end", fitToView);
    
    // Also fit after a short delay in case simulation is fast
    const fitTimeout = setTimeout(fitToView, 500);

    return () => {
      simulation.stop();
      clearTimeout(fitTimeout);
    };
  }, [nodes, links, dimensions, nodeSize, linkDistance, chargeStrength, linkColor, showLabels, showTooltips, draggable]);

  if (nodes.length === 0) {
    return (
      <div className="h-full w-full flex flex-col">
        {title && (
          <div className="px-4 pt-4 pb-2">
            <Typography variant="h6" className="text-content-primary">{title}</Typography>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FaProjectDiagram className="text-6xl text-content-tertiary mx-auto mb-4" />
            <Typography variant="h6" className="text-content-secondary">
              Aucun document disponible
            </Typography>
            <Typography variant="text-sm" className="text-content-tertiary mt-2">
              Les documents seront affichés ici une fois disponibles
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full relative" ref={containerRef}>
      {title && (
        <div className="absolute top-2 left-2 bg-surface-elevated/90 rounded-lg px-3 py-1 z-10 shadow-arc-sm">
          <Typography variant="text-sm" className="font-semibold">{title}</Typography>
        </div>
      )}
      
      <svg ref={svgRef} width={dimensions.width} height={dimensions.height} className="w-full h-full" />
      
      {tooltip && (
        <div
          className={`absolute bg-surface-elevated text-content-primary text-sm rounded-lg px-3 py-2 pointer-events-none ${zIndexClass(Z_INDEX.TOOLTIP)} shadow-arc-lg border border-arc-border`}
          style={{ left: `${tooltip.x + 10}px`, top: `${tooltip.y + 10}px`, whiteSpace: "pre-line" }}
        >
          {tooltip.content}
        </div>
      )}
      
      {/* Statistics */}
      {(visual.showLegend ?? true) && (
        <div className="absolute bottom-4 left-4 bg-surface-elevated rounded-lg shadow-arc-lg p-4 text-sm text-content-primary border border-arc-border">
          <div className="font-semibold mb-2 text-content-primary">Statistiques</div>
          <div className="text-content-secondary">Nœuds: {nodes.length}</div>
          <div className="text-content-secondary">Liens: {links.length}</div>
        </div>
      )}

      {/* Legend */}
      {(visual.showLegend ?? true) && (() => {
        const uniqueTypes = Array.from(new Set(nodes.map((n) => n.type))).sort();
        if (uniqueTypes.length > 0) {
          return (
            <div className="absolute top-4 right-4 bg-surface-elevated rounded-lg shadow-arc-lg p-4 text-sm max-w-xs border border-arc-border">
              <div className="font-semibold mb-3 text-content-primary">Types de nœuds</div>
              <div className="space-y-2">
                {uniqueTypes.map((type) => (
                  <div key={type} className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full border-2 border-arc-border"
                      style={{ backgroundColor: getNodeColorByType(type) }}
                    />
                    <span className="text-content-secondary capitalize font-medium">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return null;
      })()}

      <PostDetailModal
        open={!!selectedContent}
        onClose={() => setSelectedContent(null)}
        content={selectedContent}
        sentiment={(selectedContent?.extra?.sentiment as any)?.positive}
      />
    </div>
  );
};

export default GraphWidget;
